import Vue from 'vue'

import { Match, Team } from '@/types/match'

import { apiBaseUrl } from '@/firebase-config'

import { currentPeriodNumber, periodText } from 'match-central-shared/dist/game-clock'
import { activePenalties } from 'match-central-shared/dist/match-penalties'
import { prettifyTimeFromNumeric } from 'match-central-shared/dist/utility/time'

/**
 * https://flaviocopes.com/how-to-determine-date-is-today-javascript/
 */
function isToday(someDate: Date) {
  const today = new Date()

  return (
    someDate.getDate() === today.getDate() &&
    someDate.getMonth() === today.getMonth() &&
    someDate.getFullYear() === today.getFullYear()
  )
}

export default Vue.extend({
  props: {
    matchProp: {
      type: Object,
      required: false,
    },
  },

  computed: {
    /**
     * Match computed-method
     * used by many other computed method
     *
     * Can be overwritten by implementing component!!!
     * @returns
     */
    match(): Match | null {
      return this.matchProp
    },

    /**
     * Home team
     *
     * @returns
     */
    homeTeam(): Team | null {
      if (!this.match) {
        return null
      }

      return {
        name: this.match.hName,
        abbr: this.match.hAbbr,
        score: this.scores.h,
        penalties: activePenalties(this.match, 'h', this.$store.state.match.realtime.timestamp).map(
          (p) => ({
            ...p,
            remainingPretty: prettifyTimeFromNumeric(p.remaining, { ceil: true }),
          })
        ),
      }
    },

    /**
     * Visitor team
     *
     * @returns
     */
    visitorTeam(): Team | null {
      if (!this.match) {
        return null
      }

      return {
        name: this.match.vName,
        abbr: this.match.vAbbr,
        score: this.scores.v,
        penalties: activePenalties(this.match, 'v', this.$store.state.match.realtime.timestamp).map(
          (p) => ({
            ...p,
            remainingPretty: prettifyTimeFromNumeric(p.remaining, { ceil: true }),
          })
        ),
      }
    },

    /**
     * Scores
     * @returns
     */
    scores(): { h: number; v: number } {
      if (!this.match) {
        return {
          h: 0,
          v: 0,
        }
      }

      return {
        h: this.match.hScore || 0,
        v: this.match.vScore || 0,
      }
    },

    /**
     * Kick off date object
     *
     * @returns {Date|null}
     */
    kickoff(): Date | null {
      if (!this.match) {
        return null
      }

      if (!this.match.kickoff) {
        return null
      }

      return this.match.kickoff.toDate()
    },

    kickoffDate(): string {
      const ko = this.kickoff
      if (!ko) {
        return ''
      }

      return ko.toLocaleDateString('sv-SE')
    },

    kickoffTime(): string {
      if (!this.kickoff) {
        return ''
      }

      const output = []

      output.push(String(this.kickoff.getHours()).padStart(2, '0'))
      output.push(':')
      // https://www.codegrepper.com/code-examples/javascript/javascript+format+date+yyyy-mm-dd+hh+mm+ss
      output.push(String(this.kickoff.getMinutes()).padStart(2, '0'))

      return output.join('')
    },

    /**
     * API link
     * @returns {string}
     */
    apiLink(): string {
      // Guard no match
      if (!this.match) {
        return ''
      }

      // Guard no user set in state of vuex store
      if (!this.$store.state.user) {
        return ''
      }

      return `${apiBaseUrl}/match/${this.match.id}?user=${this.$store.state.user.uid}`
    },

    /**
     *
     * @returns
     */
    currentPeriodNumber(): number {
      if (!this.match) {
        return 1
      }

      return currentPeriodNumber(this.match)
    },

    /**
     * Get period text
     */
    periodText(): string {
      if (!this.match) {
        return ''
      }

      return periodText(this.match)
    },
    // End of computed
  },

  methods: {
    /**
     * Kickoff formatted
     *
     * @param showToday
     * @returns
     */
    kickoffFormatted(showToday = false): string {
      if (!this.kickoff) {
        return ''
      }

      const output = []

      // Alway prepend date if not today
      if (!isToday(this.kickoff)) {
        output.push(this.kickoffDate + ' ')
      }
      // Prepend 'today' if today and wants to show text
      else if (showToday) {
        output.push('Today ')
      }

      output.push(this.kickoffTime)

      return output.join('')
    },
  },
})
